<template>
  <div class="revenue-report">

    <!-- ── FILTER BAR ── -->
    <b-card class="filter-card mb-3">
      <b-card-text>
        <b-row class="align-items-end">

          <!-- approved_at_from -->
          <b-col
            md="3"
            cols="12"
            class="mb-2 mb-md-0"
          >
            <b-form-group
              :label="'من تاريخ القبض'"
              class="mb-0"
            >
              <datepicker
                v-model="filters.approved_at_from"
                :calendar-button="true"
                :clear-button="true"
                :bootstrap-styling="true"
                placeholder="YYYY-MM-DD"
              />
            </b-form-group>
          </b-col>

          <!-- approved_at_to -->
          <b-col
            md="3"
            cols="12"
            class="mb-2 mb-md-0"
          >
            <b-form-group
              :label="'الى تاريخ القبض'"
              class="mb-0"
            >
              <datepicker
                v-model="filters.approved_at_to"
                :calendar-button="true"
                :clear-button="true"
                :bootstrap-styling="true"
                placeholder="YYYY-MM-DD"
              />
            </b-form-group>
          </b-col>

          <!-- doctor_id -->
          <b-col
            md="3"
            cols="12"
            class="mb-2 mb-md-0"
          >
            <b-form-group
              :label="$t('common.doctor-name')"
              label-for="revenue-doctor-filter"
              class="mb-0"
            >
              <AutoComplete
                id="revenue-doctor-filter"
                v-model="filters.doctor_id"
                endpoint="doctor"
                param-query="search"
                text-title="name"
                :serializer="(val) => val.id"
                :placeholder="$t('doctor-filter')"
                :clearable="true"
                :autocomplete-delay="300"
              />
            </b-form-group>
          </b-col>

          <!-- type -->
          <b-col
            md="3"
            cols="12"
            class="mb-2 mb-md-0"
          >
            <b-form-group
              :label="$t('common.type') || 'النوع'"
              label-for="revenue-type-filter"
              class="mb-0"
            >
              <v-select
                id="revenue-type-filter"
                v-model="filters.type"
                :options="invoiceTypeOptions"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :clearable="true"
                :searchable="true"
                label="label"
                :reduce="(option) => option.value"
                :placeholder="$t('common.all-types') || 'All Types'"
              />
            </b-form-group>
          </b-col>

          <!-- service_type_id -->
          <b-col
            md="3"
            cols="12"
            class="mb-2 mb-md-0"
          >
            <b-form-group
              :label="$t('hospital.ServiceTypes') || 'نوع الخدمة'"
              label-for="revenue-service-type-filter"
              class="mb-0"
            >
              <AutoComplete
                id="revenue-service-type-filter"
                v-model="filters.service_type_id"
                endpoint="serviceTypes"
                param-query="search"
                text-title="name"
                :serializer="(val) => val.id"
                :placeholder="$t('hospital.ServiceTypes') || 'نوع الخدمة'"
                :clearable="true"
                :autocomplete-delay="300"
              />
            </b-form-group>
          </b-col>

          <!-- invoice_no -->
          <b-col
            md="3"
            cols="12"
            class="mb-2 mb-md-0"
          >
            <b-form-group
              :label="$t('common.invoice-number') || 'رقم الفاتورة'"
              class="mb-0"
            >
              <b-form-input
                v-model="filters.invoice_no"
                type="number"
                :placeholder="$t('common.invoice-number') || 'رقم الفاتورة'"
              />
            </b-form-group>
          </b-col>

          <!-- patient_name -->
          <b-col
            md="3"
            cols="12"
            class="mb-2 mb-md-0"
          >
            <b-form-group
              :label="$t('common.patient-name') || 'اسم المريض'"
              class="mb-0"
            >
              <b-form-input
                v-model="filters.patient_name"
                :placeholder="$t('common.patient-name') || 'اسم المريض'"
              />
            </b-form-group>
          </b-col>

          <!-- actions -->
          <b-col
            md="3"
            cols="12"
            class="mb-2 mb-md-0 d-flex align-items-end"
          >
            <div
              class="d-flex"
              style="gap: 0.5rem; width: 100%"
            >
              <b-button
                variant="primary"
                :disabled="loading"
                class="flex-grow-1"
                @click="applyFilters"
              >
                <b-spinner
                  v-if="loading"
                  small
                  class="mr-50"
                />
                <feather-icon
                  v-else
                  icon="SearchIcon"
                  size="14"
                  class="mr-25"
                />
                {{ $t('common.search') || 'بحث' }}
              </b-button>
              <b-button
                variant="outline-secondary"
                :disabled="loading"
                @click="resetFilters"
              >
                <feather-icon
                  icon="RefreshCwIcon"
                  size="14"
                />
              </b-button>
              <b-button
                variant="outline-primary"
                :disabled="loading"
                @click="printDaysReport"
              >
                <feather-icon
                  icon="PrinterIcon"
                  size="14"
                  class="mr-25"
                />
                طباعة الأيام
              </b-button>
              <b-button
                variant="outline-success"
                :disabled="loading"
                @click="printAllDetailsReport"
              >
                <feather-icon
                  icon="PrinterIcon"
                  size="14"
                  class="mr-25"
                />
                طباعة الكل بالتفاصيل
              </b-button>
            </div>
          </b-col>
        </b-row>

        <!-- active filter pills -->
        <div
          v-if="activeFilterPills.length"
          class="active-pills mt-2"
        >
          <span
            v-for="pill in activeFilterPills"
            :key="pill.key"
            class="filter-pill"
          >
            <feather-icon
              :icon="pill.icon"
              size="11"
              class="mr-25"
            />
            {{ pill.label }}
          </span>
        </div>
      </b-card-text>
    </b-card>

    <!-- ── STATISTICS STRIP ── -->
    <div
      v-if="stats"
      class="stats-grid mb-3"
    >
      <div class="stat-card">
        <div class="stat-icon stat-icon--blue">
          <feather-icon
            icon="FileTextIcon"
            size="16"
          />
        </div>
        <div class="stat-body">
          <span class="stat-value">{{ stats.invoice_count }}</span>
          <span class="stat-label">{{ $t('common.invoices') || 'الفواتير' }}</span>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon stat-icon--green">
          <feather-icon
            icon="DollarSignIcon"
            size="16"
          />
        </div>
        <div class="stat-body">
          <span class="stat-value">{{ formatIQD(stats.total_amount) }}</span>
          <span class="stat-label">{{ $t('common.total-amount') || 'إجمالي المبلغ' }}</span>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon stat-icon--teal">
          <feather-icon
            icon="CheckCircleIcon"
            size="16"
          />
        </div>
        <div class="stat-body">
          <span class="stat-value">{{ formatIQD(stats.total_net_amount) }}</span>
          <span class="stat-label">{{ $t('common.net-amount') || 'الصافي' }}</span>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon stat-icon--amber">
          <feather-icon
            icon="TagIcon"
            size="16"
          />
        </div>
        <div class="stat-body">
          <span class="stat-value">{{ formatIQD(stats.total_discount) }}</span>
          <span class="stat-label">{{ $t('common.discount') || 'الخصم' }}</span>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon stat-icon--red">
          <feather-icon
            icon="RotateCcwIcon"
            size="16"
          />
        </div>
        <div class="stat-body">
          <span class="stat-value">{{ formatIQD(stats.total_refund) }}</span>
          <span class="stat-label">الاسترجاع</span>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon stat-icon--purple">
          <feather-icon
            icon="UserIcon"
            size="16"
          />
        </div>
        <div class="stat-body">
          <span class="stat-value">{{ formatIQD(stats.total_doctor_amount) }}</span>
          <span class="stat-label">حصة الطبيب</span>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon stat-icon--indigo">
          <feather-icon
            icon="HomeIcon"
            size="16"
          />
        </div>
        <div class="stat-body">
          <span class="stat-value">{{ formatIQD(stats.total_hospital_revenue) }}</span>
          <span class="stat-label">إيراد المستشفى</span>
        </div>
      </div>
    </div>

    <!-- ── DAILY BREAKDOWN TABLE ── -->
    <b-card>
      <b-card-text>
        <div class="d-flex justify-content-between align-items-center mb-3">
          <b-badge
            v-if="dailyRows.length && !loading"
            variant="light-primary"
            pill
          >
            {{ dailyRows.length }} {{ 'يوم' }}
          </b-badge>
        </div>

        <b-table
          :items="dailyRows"
          :fields="tableColumns"
          :busy="loading"
          small
          responsive
          striped
          hover
          show-empty
          :empty-text="$t('common.no-data') || 'لا توجد بيانات'"
        >
          <template #table-busy>
            <div class="text-center my-4">
              <b-spinner
                variant="primary"
                class="mr-1"
              />
              <span class="text-muted">{{ $t('common.loading') || 'جاري التحميل...' }}</span>
            </div>
          </template>

          <template #cell(date)="data">
            <div class="d-flex align-items-center">
              <div class="date-dot mr-50" />
              <span class="font-weight-bold">{{ data.item.date }}</span>
            </div>
          </template>

          <template #cell(invoice_count)="data">
            <b-badge
              variant="light-info"
              pill
            >
              {{ data.item.invoice_count }}
            </b-badge>
          </template>

          <template #cell(total_amount)="data">
            <span class="font-weight-bold">{{ formatIQD(data.item.total_amount) }}</span>
          </template>

          <template #cell(total_net_amount)="data">
            <span class="text-success font-weight-bold">{{ formatIQD(data.item.total_net_amount) }}</span>
          </template>

          <!-- FIX: Added missing total_discount cell slot with proper formatting -->
          <template #cell(total_discount)="data">
            <span :class="data.item.total_discount > 0 ? 'text-warning font-weight-bold' : 'text-muted'">
              {{ formatIQD(data.item.total_discount) }}
            </span>
          </template>

          <template #cell(total_doctor_amount)="data">
            <span class="text-muted">{{ formatIQD(data.item.total_doctor_amount) }}</span>
          </template>

          <template #cell(total_refund)="data">
            <span :class="data.item.total_refund > 0 ? 'text-danger' : 'text-muted'">
              {{ formatIQD(data.item.total_refund) }}
            </span>
          </template>

          <template #cell(total_hospital_revenue)="data">
            <span class="text-primary font-weight-bold">{{ formatIQD(data.item.total_hospital_revenue) }}</span>
          </template>

          <template #cell(actions)="data">
            <b-button
              size="sm"
              variant="flat-primary"
              class="btn-icon"
              @click="openDetailsModal(data.item)"
            >
              <feather-icon
                icon="EyeIcon"
                size="15"
              />
            </b-button>
          </template>
        </b-table>
      </b-card-text>
    </b-card>

    <!-- ── DETAILS MODAL ── -->
    <b-modal
      v-model="showDetailsModal"
      size="xl"
      :title="selectedDay ? selectedDay.date : ($t('common.details') || 'تفاصيل')"
      hide-footer
      scrollable
    >
      <div v-if="selectedDay">
        <b-row class="mb-3">
          <b-col
            md="2"
            cols="6"
            class="mb-2"
          >
            <div class="modal-stat-card">
              <span class="modal-stat-value">{{ selectedDay.invoice_count }}</span>
              <span class="modal-stat-label">{{ $t('common.count') || 'الفواتير' }}</span>
            </div>
          </b-col>
          <b-col
            md="2"
            cols="6"
            class="mb-2"
          >
            <div class="modal-stat-card">
              <span class="modal-stat-value text-primary">{{ formatIQD(selectedDay.total_amount) }}</span>
              <span class="modal-stat-label">{{ $t('common.total-amount') || 'إجمالي المبلغ' }}</span>
            </div>
          </b-col>
          <b-col
            md="2"
            cols="6"
            class="mb-2"
          >
            <div class="modal-stat-card">
              <span class="modal-stat-value text-success">{{ formatIQD(selectedDay.total_net_amount) }}</span>
              <span class="modal-stat-label">{{ $t('common.net-amount') || 'الصافي' }}</span>
            </div>
          </b-col>
          <b-col
            md="2"
            cols="6"
            class="mb-2"
          >
            <div class="modal-stat-card">
              <span class="modal-stat-value text-warning">{{ formatIQD(selectedDay.total_doctor_amount) }}</span>
              <span class="modal-stat-label">حصة الطبيب</span>
            </div>
          </b-col>
          <b-col
            md="2"
            cols="6"
            class="mb-2"
          >
            <div class="modal-stat-card">
              <span class="modal-stat-value text-danger">{{ formatIQD(selectedDay.total_refund) }}</span>
              <span class="modal-stat-label">الاسترجاع</span>
            </div>
          </b-col>
          <b-col
            md="2"
            cols="6"
            class="mb-2"
          >
            <div class="modal-stat-card">
              <span class="modal-stat-value text-info">{{ formatIQD(selectedDay.total_hospital_revenue) }}</span>
              <span class="modal-stat-label">إيراد المستشفى</span>
            </div>
          </b-col>
        </b-row>

        <b-table
          :items="selectedDay.invoices || []"
          :fields="detailsColumns"
          small
          responsive
          striped
          hover
          show-empty
          :empty-text="$t('common.no-data') || 'لا توجد بيانات'"
        >
          <template #cell(invoice_no)="data">
            <span class="font-weight-bold text-primary">#{{ data.item.invoice_no }}</span>
          </template>

          <template #cell(type_label)="data">
            <b-badge
              :variant="typeVariant(data.item.type)"
              pill
              style="font-size: 0.75rem"
            >
              {{ data.item.type_label }}
            </b-badge>
          </template>

          <template #cell(patient_name)="data">
            <div class="d-flex align-items-center">
              <div class="patient-avatar mr-75">
                {{ avatarInitials(data.item.patient_name) }}
              </div>
              <div>
                <p
                  class="mb-0 font-weight-bold"
                  style="font-size: 0.85rem; line-height: 1.2"
                >
                  {{ data.item.patient_name }}
                </p>
                <small class="text-muted">{{ $t('common.card') || 'بطاقة' }} #{{ data.item.patient_card_no }}</small>
              </div>
            </div>
          </template>

          <template #cell(service_name)="data">
            <span v-if="data.item.service_name">{{ data.item.service_name }}</span>
            <span v-if="data.item.surgery_name">{{ data.item.surgery_name }}</span>
          </template>

          <template #cell(service_type)="data">
            <b-badge
              :variant="serviceTypeVariant(data.item.service_type)"
              pill
              style="font-size: 0.72rem"
            >
              {{ data.item.service_type || '—' }}
            </b-badge>
          </template>

          <template #cell(amount)="data">
            <span>{{ formatIQD(data.item.amount) }}</span>
          </template>

          <template #cell(discount)="data">
            <span :class="data.item.discount > 0 ? 'text-warning' : 'text-muted'">
              {{ formatIQD(data.item.discount) }}
            </span>
          </template>

          <template #cell(net_amount)="data">
            <span class="font-weight-bold text-success">{{ formatIQD(data.item.net_amount) }}</span>
          </template>

          <template #cell(doctor_name)="data">
            <span>{{ data.item.doctor_name || 'N/A' }}</span>
          </template>

          <template #cell(doctor_amount)="data">
            <span>{{ formatIQD(data.item.doctor_amount) }}</span>
          </template>

          <template #cell(refund_amount)="data">
            <span :class="data.item.refund_amount > 0 ? 'text-danger' : 'text-muted'">
              {{ formatIQD(data.item.refund_amount) }}
            </span>
          </template>

          <template #cell(hospital_revenue)="data">
            <span class="font-weight-bold text-primary">{{ formatIQD(data.item.hospital_revenue) }}</span>
          </template>

          <template #cell(approved_at)="data">
            <span
              class="text-nowrap text-muted"
              style="font-size: 0.8rem"
            >
              {{ formatDateTime(data.item.approved_at) }}
            </span>
          </template>
        </b-table>

        <div class="text-right mt-3">
          <b-button
            variant="outline-secondary"
            @click="closeDetailsModal"
          >
            {{ $t('common.close') || 'إغلاق' }}
          </b-button>
        </div>
      </div>
    </b-modal>

    <!-- ── PDF PRINT TEMPLATE ── -->
    <!--
      manual-pagination="true": we own every page boundary via html2pdf__page-break.
      Each page is a sibling <div class="pdf-page"> so the engine never auto-inserts
      whitespace between pages. No padding on the outer <section>; padding lives
      inside each .pdf-page instead.
    -->
    <vue-html2pdf
      ref="revenueHtml2Pdf"
      :show-layout="false"
      :float-layout="true"
      :enable-download="false"
      :preview-modal="true"
      :paginate-elements-by-height="1400"
      filename="revenue-report"
      :pdf-quality="2"
      :manual-pagination="true"
      pdf-format="a4"
      pdf-orientation="portrait"
      pdf-content-width="780px"
    >
      <section
        v-if="printPayload"
        slot="pdf-content"
        class="revenue-pdf-content font-modify"
      >
        <!-- ══ PAGE 1: header + summary + first data chunk ══ -->
        <div class="pdf-page">
          <!-- Report header -->
          <div class="pdf-header mb-2">
            <h4 class="mb-1">
              {{ printPayload.title }}
            </h4>
            <small>تاريخ الطباعة: {{ printPayload.printedAt }}</small>
          </div>

          <!-- Active filters -->
          <div
            class="mb-2"
            style="font-size:11px"
          >
            <strong>الفلاتر: </strong>
            <span v-if="printPayload.filters.length">{{ printPayload.filters.join(' | ') }}</span>
            <span v-else>بدون فلاتر</span>
          </div>

          <!-- Summary stats row -->
          <table class="pdf-table pdf-summary-table mb-3">
            <thead>
              <tr>
                <th>عدد الفواتير</th>
                <th>إجمالي المبلغ</th>
                <th>الصافي</th>
                <th>الخصم</th>
                <th>الاسترجاع</th>
                <th>حصة الطبيب</th>
                <th>إيراد المستشفى</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{{ (stats && stats.invoice_count) || 0 }}</td>
                <td>{{ formatIQD(stats && stats.total_amount) }}</td>
                <td>{{ formatIQD(stats && stats.total_net_amount) }}</td>
                <td>{{ formatIQD(stats && stats.total_discount) }}</td>
                <td>{{ formatIQD(stats && stats.total_refund) }}</td>
                <td>{{ formatIQD(stats && stats.total_doctor_amount) }}</td>
                <td>{{ formatIQD(stats && stats.total_hospital_revenue) }}</td>
              </tr>
            </tbody>
          </table>

          <!-- First chunk of data (page 1) — Days mode -->
          <template v-if="printPayload.mode === 'days' && chunkedDailyRows.length">
            <table class="pdf-table">
              <thead>
                <tr>
                  <th>#</th><th>التاريخ</th><th>الفواتير</th><th>المبلغ</th>
                  <th>الصافي</th><th>الخصم</th><th>حصة الطبيب</th>
                  <th>الاسترجاع</th><th>إيراد المستشفى</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, rowIndex) in chunkedDailyRows[0]"
                  :key="`d0-${rowIndex}`"
                >
                  <td>{{ rowIndex + 1 }}</td>
                  <td>{{ row.date || '-' }}</td>
                  <td>{{ row.invoice_count || 0 }}</td>
                  <td>{{ formatIQD(row.total_amount) }}</td>
                  <td>{{ formatIQD(row.total_net_amount) }}</td>
                  <td>{{ formatIQD(row.total_discount) }}</td>
                  <td>{{ formatIQD(row.total_doctor_amount) }}</td>
                  <td>{{ formatIQD(row.total_refund) }}</td>
                  <td>{{ formatIQD(row.total_hospital_revenue) }}</td>
                </tr>
              </tbody>
            </table>
          </template>

          <!-- First chunk of data (page 1) — Details mode -->
          <template v-if="printPayload.mode !== 'days' && chunkedDetailsRows.length">
            <table class="pdf-table">
              <thead>
                <tr>
                  <th>#</th><th>اليوم</th><th>الفاتورة</th><th>النوع</th>
                  <th>المريض</th><th>الخدمة</th><th>المبلغ</th><th>الخصم</th>
                  <th>الصافي</th><th>الطبيب</th><th>إيراد المستشفى</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, rowIndex) in chunkedDetailsRows[0]"
                  :key="`e0-${rowIndex}`"
                >
                  <td>{{ rowIndex + 1 }}</td>
                  <td>{{ row.day_date || '-' }}</td>
                  <td>#{{ row.invoice_no || '-' }}</td>
                  <td>{{ row.type_label || '-' }}</td>
                  <td>{{ row.patient_name || '-' }}</td>
                  <td>{{ row.service_name || row.surgery_name || '-' }}</td>
                  <td>{{ formatIQD(row.amount) }}</td>
                  <td>{{ formatIQD(row.discount) }}</td>
                  <td>{{ formatIQD(row.net_amount) }}</td>
                  <td>{{ row.doctor_name || '-' }}</td>
                  <td>{{ formatIQD(row.hospital_revenue) }}</td>
                </tr>
              </tbody>
            </table>
          </template>
        </div><!-- end page 1 -->

        <!-- ══ PAGES 2+: one pdf-page per remaining chunk ══ -->

        <!-- Days mode — remaining chunks -->
        <template v-if="printPayload.mode === 'days'">
          <template v-for="(chunk, chunkIndex) in chunkedDailyRows">
            <template v-if="chunkIndex > 0">
              <!-- page-break marker: html2pdf sees this and starts a new page -->
              <div
                :key="`pb-d-${chunkIndex}`"
                class="html2pdf__page-break"
              />
              <div
                :key="`pg-d-${chunkIndex}`"
                class="pdf-page"
              >
                <!-- compact continuation header -->
                <div class="pdf-continuation-header mb-1">
                  <span>{{ printPayload.title }}</span>
                  <span>صفحة {{ chunkIndex + 1 }}</span>
                </div>
                <table class="pdf-table">
                  <thead>
                    <tr>
                      <th>#</th><th>التاريخ</th><th>الفواتير</th><th>المبلغ</th>
                      <th>الصافي</th><th>الخصم</th><th>حصة الطبيب</th>
                      <th>الاسترجاع</th><th>إيراد المستشفى</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(row, rowIndex) in chunk"
                      :key="`dc-${chunkIndex}-${rowIndex}`"
                    >
                      <td>{{ chunkIndex * printPayload.rowsPerPage + rowIndex + 1 }}</td>
                      <td>{{ row.date || '-' }}</td>
                      <td>{{ row.invoice_count || 0 }}</td>
                      <td>{{ formatIQD(row.total_amount) }}</td>
                      <td>{{ formatIQD(row.total_net_amount) }}</td>
                      <td>{{ formatIQD(row.total_discount) }}</td>
                      <td>{{ formatIQD(row.total_doctor_amount) }}</td>
                      <td>{{ formatIQD(row.total_refund) }}</td>
                      <td>{{ formatIQD(row.total_hospital_revenue) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </template>
          </template>
        </template>

        <!-- Details mode — remaining chunks -->
        <template v-else>
          <template v-for="(chunk, chunkIndex) in chunkedDetailsRows">
            <template v-if="chunkIndex > 0">
              <div
                :key="`pb-e-${chunkIndex}`"
                class="html2pdf__page-break"
              />
              <div
                :key="`pg-e-${chunkIndex}`"
                class="pdf-page"
              >
                <div class="pdf-continuation-header mb-1">
                  <span>{{ printPayload.title }}</span>
                  <span>صفحة {{ chunkIndex + 1 }}</span>
                </div>
                <table class="pdf-table">
                  <thead>
                    <tr>
                      <th>#</th><th>اليوم</th><th>الفاتورة</th><th>النوع</th>
                      <th>المريض</th><th>الخدمة</th><th>المبلغ</th><th>الخصم</th>
                      <th>الصافي</th><th>الطبيب</th><th>إيراد المستشفى</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(row, rowIndex) in chunk"
                      :key="`ec-${chunkIndex}-${rowIndex}`"
                    >
                      <td>{{ chunkIndex * printPayload.rowsPerPage + rowIndex + 1 }}</td>
                      <td>{{ row.day_date || '-' }}</td>
                      <td>#{{ row.invoice_no || '-' }}</td>
                      <td>{{ row.type_label || '-' }}</td>
                      <td>{{ row.patient_name || '-' }}</td>
                      <td>{{ row.service_name || row.surgery_name || '-' }}</td>
                      <td>{{ formatIQD(row.amount) }}</td>
                      <td>{{ formatIQD(row.discount) }}</td>
                      <td>{{ formatIQD(row.net_amount) }}</td>
                      <td>{{ row.doctor_name || '-' }}</td>
                      <td>{{ formatIQD(row.hospital_revenue) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </template>
          </template>
        </template>

      </section>
    </vue-html2pdf>

  </div>
</template>

<script>
import {
  BCard, BCardText, BRow, BCol, BFormGroup, BFormInput,
  BButton, BTable, BSpinner, BModal, BBadge,
} from 'bootstrap-vue'
import Datepicker from 'vuejs-datepicker'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import AutoComplete from '@/components/common/AutoComplete.vue'
import VueHtml2pdf from 'vue-html2pdf'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'

// How many rows to place per printed page before forcing a page break
const ROWS_PER_PRINT_PAGE = 20

const getCurrentMonthDateRange = () => {
  const now = new Date()
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0)
  return { startOfMonth, endOfMonth }
}

export default {
  name: 'RevenueReport',

  components: {
    BCard,
    BCardText,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BButton,
    BTable,
    BSpinner,
    BModal,
    BBadge,
    Datepicker,
    AutoComplete,
    VueHtml2pdf,
    vSelect,
  },

  data() {
    const { startOfMonth, endOfMonth } = getCurrentMonthDateRange()
    return {
      filters: {
        approved_at_from: startOfMonth,
        approved_at_to: endOfMonth,
        paid_at_from: null,
        paid_at_to: null,
        invoice_no: null,
        type: null,
        doctor_id: null,
        service_type_id: null,
        patient_name: '',
      },

      invoiceTypeOptions: [],
      doctorNameById: {},
      serviceTypeNameById: {},
      loading: false,
      reportData: null,
      showDetailsModal: false,
      selectedDay: null,
      printPayload: null,

      tableColumns: [
        { key: 'date', label: this.$t('common.date') || 'التاريخ', sortable: true },
        { key: 'invoice_count', label: this.$t('common.count') || 'الفواتير', sortable: true },
        { key: 'total_amount', label: this.$t('common.amount') || 'المبلغ', sortable: true },
        { key: 'total_net_amount', label: this.$t('common.net-amount') || 'الصافي', sortable: true },
        { key: 'total_discount', label: this.$t('common.discount') || 'الخصم', sortable: true },
        { key: 'total_doctor_amount', label: 'حصة الطبيب', sortable: true },
        { key: 'total_refund', label: 'الاسترجاع', sortable: true },
        { key: 'total_hospital_revenue', label: 'إيراد المستشفى', sortable: true },
        { key: 'actions', label: '' },
      ],

      detailsColumns: [
        { key: 'invoice_no', label: this.$t('common.invoice-number') || 'الفاتورة' },
        { key: 'type_label', label: this.$t('common.type') || 'النوع' },
        { key: 'patient_name', label: this.$t('common.patient-name') || 'المريض' },
        { key: 'service_name', label: 'الخدمة' },
        { key: 'service_type', label: 'نوع الخدمة' },
        { key: 'amount', label: this.$t('common.amount') || 'المبلغ' },
        { key: 'discount', label: this.$t('common.discount') || 'الخصم' },
        { key: 'net_amount', label: this.$t('common.net-amount') || 'الصافي' },
        { key: 'doctor_name', label: 'الطبيب' },
        { key: 'doctor_amount', label: 'حصة الطبيب' },
        { key: 'refund_amount', label: 'الاسترجاع' },
        { key: 'hospital_revenue', label: 'إيراد المستشفى' },
        { key: 'approved_at', label: 'تاريخ الاعتماد' },
      ],
    }
  },

  computed: {
    stats() {
      return this.reportData?.statistics || null
    },
    dailyRows() {
      if (!this.reportData || !Array.isArray(this.reportData.daily_breakdown)) return []
      return this.reportData.daily_breakdown
    },

    // Split daily rows into chunks for paginated PDF printing
    chunkedDailyRows() {
      if (!this.printPayload) return []
      return this.chunkArray(this.printPayload.dailyRows, this.printPayload.rowsPerPage)
    },

    // Split details rows into chunks for paginated PDF printing
    chunkedDetailsRows() {
      if (!this.printPayload) return []
      return this.chunkArray(this.printPayload.detailsRows || [], this.printPayload.rowsPerPage)
    },

    activeFilterPills() {
      const pills = []
      if (this.filters.approved_at_from || this.filters.approved_at_to) {
        const from = this.filters.approved_at_from ? this.fmtDate(this.filters.approved_at_from) : '...'
        const to = this.filters.approved_at_to ? this.fmtDate(this.filters.approved_at_to) : '...'
        pills.push({ key: 'approved', icon: 'CalendarIcon', label: `اعتماد: ${from} → ${to}` })
      }
      if (this.filters.invoice_no) pills.push({ key: 'inv', icon: 'HashIcon', label: `فاتورة #${this.filters.invoice_no}` })
      if (this.filters.doctor_id) {
        const doctorName = this.doctorNameById[this.filters.doctor_id]
        pills.push({ key: 'doctor', icon: 'UserIcon', label: `${this.$t('common.doctor-name') || 'طبيب'}: ${doctorName || `#${this.filters.doctor_id}`}` })
      }
      if (this.filters.type) pills.push({ key: 'type', icon: 'LayersIcon', label: this.formatInvoiceType(this.filters.type) })
      if (this.filters.service_type_id) {
        const serviceTypeName = this.serviceTypeNameById[this.filters.service_type_id]
        pills.push({ key: 'svc', icon: 'TagIcon', label: `${this.$t('hospital.ServiceTypes') || 'نوع خدمة'}: ${serviceTypeName || `#${this.filters.service_type_id}`}` })
      }
      if (this.filters.patient_name) pills.push({ key: 'patient', icon: 'UserIcon', label: this.filters.patient_name })
      return pills
    },
  },

  watch: {
    'filters.doctor_id': { immediate: true, handler(val) { this.resolveDoctorName(val) } },
    'filters.service_type_id': { immediate: true, handler(val) { this.resolveServiceTypeName(val) } },
  },

  created() {
    this.initializeInvoiceTypeOptions()
    this.getReportData()
  },

  methods: {
    // ── Utility ──────────────────────────────────────────────────────
    chunkArray(arr, size) {
      if (!arr || !arr.length) return []
      const chunks = []
      for (let i = 0; i < arr.length; i += size) {
        chunks.push(arr.slice(i, i + size))
      }
      return chunks
    },

    // ── Name resolution ──────────────────────────────────────────────
    async resolveDoctorName(doctorId) {
      if (!doctorId || this.doctorNameById[doctorId]) return
      try {
        const response = await this.$http.get(`/doctor/${doctorId}`)
        const doctorName = response?.data?.data?.name
        if (doctorName) this.$set(this.doctorNameById, doctorId, doctorName)
      } catch { /* fallback to ID */ }
    },

    async resolveServiceTypeName(serviceTypeId) {
      if (!serviceTypeId || this.serviceTypeNameById[serviceTypeId]) return
      try {
        const response = await this.$http.get(`/serviceTypes/${serviceTypeId}`)
        const serviceTypeName = response?.data?.data?.name
        if (serviceTypeName) this.$set(this.serviceTypeNameById, serviceTypeId, serviceTypeName)
      } catch { /* fallback to ID */ }
    },

    // ── Type helpers ─────────────────────────────────────────────────
    initializeInvoiceTypeOptions() {
      const types = [
        'invoice_out_clinic', 'invoice_in_clinic', 'invoice_nicu',
        'invoice_patient_card_test', 'refund', 'invoice_insurance_surgery', 'invoice_emergency',
      ]
      this.invoiceTypeOptions = [
        { value: null, label: this.$t('common.all-types') || 'All Types' },
        ...types.map(type => ({ value: type, label: this.formatInvoiceType(type) })),
      ]
    },

    formatInvoiceType(type) {
      if (!type) return '-'
      const formattedKey = type.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
      const typeMap = {
        invoice_out_clinic: 'cashiers.invoice-type-out-clinic',
        invoice_nicu: 'cashiers.invoice-type-nicu',
        invoice_patient_card_test: 'cashiers.invoice-type-patient-card-test',
        refund: 'cashiers.invoice-type-refund',
        invoice_insurance_surgery: 'cashiers.invoice-type-insurance-surgery',
        invoice_emergency: 'cashiers.invoice-type-emergency',
      }
      const translationKey = typeMap[type]
      if (translationKey) {
        const translated = this.$t(translationKey)
        if (translated && translated !== translationKey) return `${translated} (${formattedKey})`
      }
      return formattedKey
    },

    typeVariant(type) {
      const map = {
        invoice_out_clinic: 'light-success', invoice_in_clinic: 'light-info', invoice_insurance_surgery: 'light-warning', refund: 'light-danger',
      }
      return map[type] || 'light-secondary'
    },

    serviceTypeVariant(type) {
      const map = { regular: 'light-success', emergency: 'light-danger', vip: 'light-warning' }
      return map[type] || 'light-secondary'
    },

    // ── Filters ──────────────────────────────────────────────────────
    applyFilters() { this.getReportData() },

    resetFilters() {
      const { startOfMonth, endOfMonth } = getCurrentMonthDateRange()
      this.filters = {
        approved_at_from: startOfMonth,
        approved_at_to: endOfMonth,
        paid_at_from: null,
        paid_at_to: null,
        invoice_no: null,
        type: null,
        doctor_id: null,
        service_type_id: null,
        patient_name: '',
      }
      this.getReportData()
    },

    buildQueryString() {
      const params = []
      const add = (k, v) => { if (v !== null && v !== undefined && v !== '') params.push(`${k}=${encodeURIComponent(v)}`) }
      add('approved_at_from', this.fmtDate(this.filters.approved_at_from))
      add('approved_at_to', this.fmtDate(this.filters.approved_at_to))
      add('paid_at_from', this.fmtDate(this.filters.paid_at_from))
      add('paid_at_to', this.fmtDate(this.filters.paid_at_to))
      add('invoice_no', this.filters.invoice_no)
      add('type', this.filters.type)
      add('doctor_id', this.filters.doctor_id)
      add('service_type_id', this.filters.service_type_id)
      add('patient_name', this.filters.patient_name)
      return params.length ? `?${params.join('&')}` : ''
    },

    // ── Formatters ───────────────────────────────────────────────────
    fmtDate(val) {
      if (!val) return ''
      if (typeof val === 'string') return val
      const d = new Date(val)
      if (Number.isNaN(d.getTime())) return ''
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    },

    formatIQD(value) {
      const num = Number(value) || 0
      return `${new Intl.NumberFormat('en-US').format(num)} IQD`
    },

    formatDateTime(dt) {
      if (!dt) return '-'
      const d = new Date(dt)
      if (Number.isNaN(d.getTime())) return dt
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
    },

    avatarInitials(name) {
      if (!name) return '?'
      return name.split(' ').slice(0, 2).map(w => w[0]).join('')
        .toUpperCase()
    },

    getPrintTimestamp() { return new Date().toLocaleString('ar-IQ') },

    getSelectedFiltersForPrint() {
      return this.activeFilterPills.length ? this.activeFilterPills.map(p => p.label) : []
    },

    collectDetailsRows() {
      return this.dailyRows.reduce((acc, dayRow) => {
        const invoices = Array.isArray(dayRow?.invoices) ? dayRow.invoices : []
        invoices.forEach(invoice => acc.push({ ...invoice, day_date: dayRow.date || '-' }))
        return acc
      }, [])
    },

    // ── Print ────────────────────────────────────────────────────────
    runPrint(mode, detailsRows = []) {
      this.printPayload = {
        mode,
        title: mode === 'days' ? 'تقرير الإيرادات - الأيام' : 'تقرير الإيرادات - كل التفاصيل',
        printedAt: this.getPrintTimestamp(),
        filters: this.getSelectedFiltersForPrint(),
        stats: this.stats,
        dailyRows: this.dailyRows,
        detailsRows,
        rowsPerPage: ROWS_PER_PRINT_PAGE,
      }
      this.$nextTick(() => {
        setTimeout(() => {
          if (this.$refs.revenueHtml2Pdf?.generatePdf) {
            this.$refs.revenueHtml2Pdf.generatePdf()
          } else {
            window.print()
          }
        }, 150)
      })
    },

    printDaysReport() {
      if (!this.dailyRows.length) {
        this.$toast({ component: ToastificationContent, position: 'top-right', props: { title: this.$t('common.warning') || 'تنبيه', text: 'لا توجد أيام متاحة للطباعة', variant: 'warning' } })
        return
      }
      this.runPrint('days')
    },

    printAllDetailsReport() {
      const detailsRows = this.collectDetailsRows()
      if (!detailsRows.length) {
        this.$toast({ component: ToastificationContent, position: 'top-right', props: { title: this.$t('common.warning') || 'تنبيه', text: 'لا توجد تفاصيل متاحة للطباعة', variant: 'warning' } })
        return
      }
      this.runPrint('details', detailsRows)
    },

    // ── Modal ────────────────────────────────────────────────────────
    openDetailsModal(dayRow) { this.selectedDay = dayRow; this.showDetailsModal = true },
    closeDetailsModal() { this.showDetailsModal = false; this.selectedDay = null },

    // ── Data ─────────────────────────────────────────────────────────
    async getReportData() {
      this.loading = true
      try {
        const response = await this.$http.get(`/reports/revenue${this.buildQueryString()}`)
        if (!response?.data?.status) {
          this.$toast({ component: ToastificationContent, position: 'top-right', props: { title: this.$t('common.error') || 'خطأ', text: response?.data?.message || this.$t('common.error-fetching-data') || 'حدث خطأ', variant: 'danger' } })
        }
        this.reportData = response?.data?.data || null
      } catch (error) {
        this.$toast({ component: ToastificationContent, position: 'top-right', props: { title: this.$t('common.error') || 'خطأ', text: error?.response?.data?.message || this.$t('common.error-fetching-data') || 'حدث خطأ', variant: 'danger' } })
        this.reportData = null
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.filter-card { border-top: 3px solid #7367f0; }
.active-pills { display: flex; flex-wrap: wrap; gap: 6px; }
.filter-pill {
  display: inline-flex; align-items: center;
  padding: 3px 10px;
  background: rgba(115, 103, 240, 0.1);
  color: #7367f0; border-radius: 20px;
  font-size: 0.78rem; font-weight: 600;
}

.stats-grid { display: flex; flex-wrap: wrap; gap: 12px; }
.stat-card {
  background: #fff; border: 1px solid #ebe9f1; border-radius: 10px;
  padding: 14px 16px; display: flex; align-items: center; gap: 12px;
  transition: box-shadow 0.2s; flex: 1;
}
.stat-card:hover { box-shadow: 0 4px 18px rgba(0,0,0,0.06); }
.stat-icon {
  width: 38px; height: 38px; border-radius: 8px;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.stat-icon--blue   { background: rgba(0,145,234,0.12);   color: #0091ea; }
.stat-icon--green  { background: rgba(40,199,111,0.12);  color: #28c76f; }
.stat-icon--teal   { background: rgba(0,207,197,0.12);   color: #00cfc5; }
.stat-icon--amber  { background: rgba(255,159,67,0.12);  color: #ff9f43; }
.stat-icon--red    { background: rgba(234,84,85,0.12);   color: #ea5455; }
.stat-icon--purple { background: rgba(115,103,240,0.12); color: #7367f0; }
.stat-icon--indigo { background: rgba(102,126,234,0.12); color: #667eea; }

.stat-body { display: flex; flex-direction: column; min-width: 0; }
.stat-value { font-size: 0.95rem; font-weight: 700; color: #5e5873; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.stat-label { font-size: 0.72rem; color: #b9b9c3; margin-top: 1px; }

.date-dot { width: 8px; height: 8px; border-radius: 50%; background: #7367f0; flex-shrink: 0; }

.modal-stat-card { background: #f8f8f8; border-radius: 8px; padding: 14px; text-align: center; }
.modal-stat-value { display: block; font-size: 1rem; font-weight: 700; color: #5e5873; }
.modal-stat-label { display: block; font-size: 0.75rem; color: #b9b9c3; margin-top: 2px; }

.patient-avatar {
  width: 30px; height: 30px; border-radius: 50%;
  background: rgba(115,103,240,0.12); color: #7367f0;
  font-size: 0.7rem; font-weight: 700;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}

/* ── PDF styles ── */
.revenue-pdf-content {
  direction: rtl;
  font-size: 12px;
  color: #111827;
  background: #fff;
  /* No padding here — padding lives inside each .pdf-page so page 2+ start flush */
}

/*
 * Each page is wrapped in .pdf-page which carries the padding.
 * html2pdf manual mode renders siblings sequentially; the .html2pdf__page-break
 * div between them signals a hard page cut, so page 2 starts at the very top
 * with no inherited spacing from page 1's content.
 */
.pdf-page {
  padding: 16px 16px 0 16px;
}

.pdf-header { border-bottom: 2px solid #7367f0; padding-bottom: 8px; }

/* Slim title bar shown at the top of pages 2+ */
.pdf-continuation-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 10px;
  color: #6b7280;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 4px;
}

.pdf-table { width: 100%; border-collapse: collapse; margin-top: 8px; }
.pdf-table th,
.pdf-table td { border: 1px solid #d1d5db; padding: 4px 6px; text-align: right; font-size: 11px; }
.pdf-table th { background: #f3f4f6; font-weight: 700; }

/* Summary table */
.pdf-summary-table { margin-bottom: 12px; }
.pdf-summary-table th { background: #ede9fe; color: #4c1d95; }

/* html2pdf page-break marker — must have zero height so page 2 is truly flush */
.html2pdf__page-break {
  page-break-before: always;
  break-before: always;
  display: block;
  height: 0;
  margin: 0;
  padding: 0;
}

/* Dark layout overrides */
.dark-layout .stat-card        { background: #283046; border-color: #3b4253; }
.dark-layout .modal-stat-card  { background: #3b4253; }
.dark-layout .stat-value,
.dark-layout .modal-stat-value { color: #d0d2d6; }

.font-modify { letter-spacing: normal !important; }
</style>
